/**
 * Self-update on an `upgrade` member directive.
 *
 * The product UI (e.g. Sift's settings page) writes
 * `metadata.directives.upgrade = { requested_version }` onto the
 * subscriber's Jobs pool-member row; the directive rides back on the
 * next discovery poll. When the requested version is newer than the
 * running build, the shuttle drains its claim loop, installs the new
 * package globally, and re-execs itself in place. On install failure
 * the old version resumes claiming — an upgrade request must never
 * take a working subscriber offline.
 *
 * The directive is self-clearing: registration on the restarted
 * process replaces the member metadata (reporting the new
 * `client_version`), which drops the pending directive.
 */

import { spawn } from 'child_process';
import { compareSemver } from '../version-check';
import type { SubscribeLogger } from '@whittlelabs/jobs';

export interface UpgradeDirective {
  requested_version: string;
  requested_at?: string;
}

/**
 * Parse and gate the upgrade directive: returns it only when it names
 * a version strictly newer than the running one and this process has
 * not already attempted it (a failed install must not retry on every
 * 2s poll; the next *distinct* requested version resets the gate).
 */
export function readUpgradeDirective(
  directives: Record<string, unknown>,
  currentVersion: string,
  attemptedVersions: Set<string>,
): UpgradeDirective | null {
  const raw = directives.upgrade;
  if (!raw || typeof raw !== 'object') return null;
  const requested = (raw as { requested_version?: unknown }).requested_version;
  if (typeof requested !== 'string' || requested.length === 0) return null;
  if (attemptedVersions.has(requested)) return null;
  if (compareSemver(currentVersion, requested) >= 0) return null;
  return {
    requested_version: requested,
    requested_at: (raw as { requested_at?: string }).requested_at,
  };
}

export interface SelfUpdateHooks {
  /** Drain the claim loop before touching the install. */
  stopLoop: () => Promise<void>;
  /** Resume claiming on the OLD version after a failed install. */
  restartLoop: () => Promise<void>;
  /** Test seam; defaults to `npm install -g <spec>`. */
  install?: (packageSpec: string) => Promise<void>;
  /** Test seam; defaults to re-exec'ing this process's argv. */
  respawn?: () => void;
}

/**
 * Execute one gated upgrade attempt. Returns true when the install
 * succeeded and the replacement process has been spawned (the caller
 * should let the current process wind down), false when the install
 * failed and the old loop was restarted.
 */
export async function performSelfUpdate(opts: {
  packageName: string;
  directive: UpgradeDirective;
  logger: SubscribeLogger;
  hooks: SelfUpdateHooks;
}): Promise<boolean> {
  const { packageName, directive, logger, hooks } = opts;
  const spec = `${packageName}@${directive.requested_version}`;
  const install = hooks.install ?? defaultInstall;
  const respawn = hooks.respawn ?? defaultRespawn;

  logger.info('upgrade directive received; draining claim loop', {
    requestedVersion: directive.requested_version,
  });
  await hooks.stopLoop();

  try {
    await install(spec);
  } catch (err) {
    logger.error('self-update install failed; resuming on the current version', {
      packageSpec: spec,
      error: err instanceof Error ? err.message : String(err),
    });
    await hooks.restartLoop();
    return false;
  }

  logger.info('self-update installed; restarting', { packageSpec: spec });
  respawn();
  return true;
}

function defaultInstall(packageSpec: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const child = spawn('npm', ['install', '-g', packageSpec], {
      stdio: ['ignore', 'pipe', 'pipe'],
    });
    let output = '';
    child.stdout.on('data', (chunk: Buffer) => (output += chunk.toString()));
    child.stderr.on('data', (chunk: Buffer) => (output += chunk.toString()));
    child.on('error', reject);
    child.on('exit', (code) => {
      if (code === 0) resolve();
      else reject(new Error(`npm install -g exited ${code}: ${output.trim().slice(-500)}`));
    });
  });
}

/**
 * Re-exec this invocation and exit THIS process immediately. The global
 * install replaced the bin script at the same path, so re-running our own argv
 * picks up the new code. The replacement is spawned detached with inherited
 * stdio and `unref`'d, so it survives our exit (and a terminal hang-up) and
 * keeps logging where we left off — then we exit cleanly.
 *
 * The old implementation waited for the child to exit before exiting itself, to
 * forward the exit code. But the child is a poll daemon that never exits, so the
 * parent lingered forever, and every upgrade stacked another idle ancestor —
 * one leaked process per upgrade generation (5 accumulated across 0.9→0.15).
 * Self-update via `npm install -g` + re-exec is a bare-process (non-supervised)
 * feature anyway; supervised deployments (containers) update by redeploying the
 * image, so there was no exit code worth forwarding.
 */
function defaultRespawn(): void {
  const child = spawn(process.execPath, process.argv.slice(1), {
    detached: true,
    stdio: 'inherit',
  });
  child.unref();
  process.exit(0);
}
